import {computed, ref} from 'vue';
import {defineStore} from 'pinia';
import {confirmSignIn, signIn, signOut, type SignInOutput} from 'aws-amplify/auth';
import * as Sentry from '@sentry/vue';
import type {Principal} from '@afroep/core/auth/principal';
import type {Locale} from '@afroep/core/domain/locale';
import type {Role} from '@afroep/core/domain/role';
import {fetchMe, updateMe} from '@/api/me';
import {applyLocale} from '@/i18n';
import {LocalizedError} from '@/i18n/localized-error';

export type SignInOutcome = 'SIGNED_IN' | 'NEW_PASSWORD_REQUIRED' | 'EMAIL_CODE_REQUIRED';

export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';

export const useSessionStore = defineStore('session', () => {
  const user = ref<Principal | null>(null);
  const status = ref<SessionStatus>('unknown');
  /** Cognito's masked rendering of the address the code went to, for the login screen. */
  const codeDestination = ref<string | null>(null);

  const isAuthenticated = computed(() => status.value === 'authenticated');
  const role = computed<Role | null>(() => user.value?.role ?? null);
  const isPlatformAdmin = computed(() => role.value === 'PLATFORM_ADMIN');
  const canManageUsers = computed(() => role.value === 'PLATFORM_ADMIN' || role.value === 'ORG_ADMIN');
  /**
   * An organisation's own settings, and only its own administrator. Deliberately not
   * `canManageUsers`: a platform admin belongs to no organisation, so there are no areas
   * that are theirs to configure — see `canConfigureCongestionArea`.
   */
  const canConfigureOwnAreas = computed(() => role.value === 'ORG_ADMIN');

  /**
   * Bumped by anything that ends the session. A `load()` reads it before its request and
   * compares afterwards: without that, a profile still in flight when the session expires
   * would land after `expire()` and mark a dead session `authenticated` — the route guard
   * would then let the reader back onto a private page holding a token nothing accepts.
   */
  let generation = 0;

  /** Loads the profile from our own API — Cognito only proves identity, not authority. */
  const load = async (): Promise<void> => {
    const started = generation;
    try {
      const principal = await fetchMe();
      // Somebody signed out, or a 401 expired the session, while this was in flight.
      // Their decision is the newer one.
      if (started !== generation) return;
      user.value = principal;
      status.value = 'authenticated';
      // The account's preference wins over whatever this browser was showing: it is the
      // same value the backend writes mail in, and the two disagreeing is the confusing case.
      applyLocale(principal.locale);
    } catch {
      if (started !== generation) return;
      user.value = null;
      status.value = 'anonymous';
    }
  };

  /**
   * Saves are chained rather than fired off in parallel. Two switches in quick succession
   * would otherwise be two in-flight PATCHes with no ordering guarantee, and the one that
   * happens to land last decides what the account says — which can be the language the
   * user moved *away* from. Every future session and every mail would then be wrong,
   * silently, and the screen would not show it.
   */
  let localeWrites: Promise<void> = Promise.resolve();

  /**
   * Remembers the language on the account, so the next mail and the next browser follow
   * it. Called after the interface has already switched — a save that fails must not
   * flip the page back under the reader, so it is reported and otherwise let go. The
   * choice still holds for this browser either way.
   */
  const changeLocale = async (locale: Locale): Promise<void> => {
    if (!user.value || user.value.locale === locale) return;
    const previous = user.value.locale;
    user.value = {...user.value, locale};

    localeWrites = localeWrites.then(async () => {
      try {
        await updateMe({locale});
      } catch (error) {
        // Only undo our own write. With two languages a later switch always happens to
        // want the same value this one would restore, so nothing observable depends on
        // this today — it is here so that adding a third language does not quietly turn
        // a failed save into a wrong one.
        if (user.value?.locale === locale) user.value = {...user.value, locale: previous};
        // Nothing is shown: the interface has already switched and this browser remembers
        // the choice, so the only thing lost is the language of the next mail. Worth
        // knowing about, not worth a toast.
        Sentry.captureException(error);
      }
    });

    await localeWrites;
  };

  /**
   * Every sign-in step lands here. A password is not enough on a pool with MFA, and the
   * invitation flow can be followed by the email code, so the outcome is whatever
   * Cognito says is still outstanding.
   */
  const resolveStep = async (result: SignInOutput): Promise<SignInOutcome> => {
    const step = result.nextStep.signInStep;
    if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') return 'NEW_PASSWORD_REQUIRED';
    if (step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
      codeDestination.value = result.nextStep.codeDeliveryDetails?.destination ?? null;
      return 'EMAIL_CODE_REQUIRED';
    }
    // Anything else — a TOTP challenge, an MFA setup prompt — is a factor this screen
    // cannot answer. Saying so beats sending the user into the app without a token.
    if (step !== 'DONE') {
      throw new LocalizedError('errors.auth.unsupportedStep');
    }
    await load();
    return 'SIGNED_IN';
  };

  const login = async (email: string, password: string): Promise<SignInOutcome> =>
    resolveStep(await signIn({username: email, password}));

  /** Second half of the invitation flow: the temporary password is replaced. */
  const completeNewPassword = async (password: string): Promise<SignInOutcome> =>
    resolveStep(await confirmSignIn({challengeResponse: password}));

  /** The second factor: the code Cognito emailed. */
  const confirmEmailCode = async (code: string): Promise<SignInOutcome> =>
    resolveStep(await confirmSignIn({challengeResponse: code}));

  /**
   * Cognito has no way to resend an MFA code, so starting over is the only remedy. The
   * pending attempt has to be dropped first, or the next `signIn` is refused.
   */
  const abandonSignIn = async (): Promise<void> => {
    codeDestination.value = null;
    try {
      await signOut();
    } catch {
      // A half-finished challenge is not a session, so there may be nothing to sign out,
      // and failing here would leave the user on a code screen with no way back.
    }
  };

  /**
   * The session ended without anybody asking — the refresh token expired or was revoked.
   * Synchronous on purpose: the caller is an API response handler, and the store has to
   * be anonymous before the route guard next runs.
   *
   * Amplify is told as well, best effort. Its copy of a dead session would otherwise
   * refuse the next `signIn` as "already signed in", and there is nothing useful to do
   * if that call fails.
   */
  const expire = (): void => {
    generation += 1;
    user.value = null;
    codeDestination.value = null;
    status.value = 'anonymous';
    void signOut().catch(() => undefined);
  };

  const logout = async (): Promise<void> => {
    generation += 1;
    await signOut();
    user.value = null;
    codeDestination.value = null;
    // The language deliberately stays as it is: whoever signs in next is more likely to
    // be the same person on the same machine than somebody wanting the default back.
    status.value = 'anonymous';
  };

  return {
    user,
    status,
    codeDestination,
    isAuthenticated,
    role,
    isPlatformAdmin,
    canManageUsers,
    canConfigureOwnAreas,
    load,
    changeLocale,
    login,
    completeNewPassword,
    confirmEmailCode,
    abandonSignIn,
    expire,
    logout,
  };
});
